import React, { useState, useEffect } from "react";
import { Button, Grid } from "@mui/material";
import { H4 } from "components/Typography";
import { useTranslation } from "react-i18next";
import AppTextField from "components/input-fields/AppTextField";
import { useFormik } from "formik";
import { KeyboardArrowDown } from "@mui/icons-material";
import { MenuItem } from "@mui/material";
import * as Yup from "yup"; // component props interface
import api from "utils/api";
import toast from "react-hot-toast";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import XLSX from "sheetjs-style";
import { QUIZ_TYPE_COLD } from "../../constants";
import { format } from "date-fns";
import CustomDateRangePicker from "components/CustomDateRangePicker";
import moment from "moment";

const ReportPerInstructor = ({ quizType }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [instructors, setInstructors] = useState([]);

  const initialValues = {
    module: -1,
    instructor: -1,
    startDate: null,
    endDate: null,
  };

  const fetchModules = async () => {
    let result = await api.getAllModules();
    if (result.success) {
      setModules([
        { id: -1, name: t("Reports.all_modules") },
        ...result.data.map((item) => ({
          id: item.id,
          name: item.module_trans[0].name,
        })),
      ]);
    } else {
      toast.error("server error");
      return;
    }
  };

  const fetchInstructors = async () => {
    let result = await api.getAllInstructors();
    if (result.success) {
      setInstructors([
        { id: -1, name: t("Reports.all_instructors") },
        ...result.data.map((item) => ({
          id: item.id,
          name: `${item.lastName} ${item.firstName}`,
        })),
      ]);
    } else {
      toast.error("server error");
      return;
    }
  };

  useEffect(() => {
    fetchModules();
    fetchInstructors();
  }, []);

  const validationSchema = Yup.object({
    group: Yup.string().required("Group is Required!"),
  });

  const { handleSubmit, touched, errors, values, setFieldValue } = useFormik({
    initialValues,
    validationSchema: validationSchema,
  });

  // if quizType changes, we need to reset the group input and disable the export button
  useEffect(() => {
    setFieldValue("group", null);
  }, [quizType]);

  const setCoverData = (coverSheet) => {
    // company name
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [[`${t("Reports.report")}:`, t("Reports.per_instructor_title")]],
      { origin: "A1" }
    );

    let currentRow = 3;
    // planning name
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.quiz_type")}:`,
          quizType === QUIZ_TYPE_COLD
            ? t("General.cold_quiz")
            : t("General.hot_quiz"),
        ],
      ],
      { origin: `A${currentRow}` }
    );
    currentRow += 1;

    // --------------------------------------------

    // report generated
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.report_generated")} ${format(
            new Date(),
            "eeee dd MMMM yyyy"
          )}`,
        ],
      ],
      { origin: "G1" }
    );
  };

  const exportToExcel = async () => {
    const module = values.module === -1 ? null : values.module;
    const instructor = values.instructor === -1 ? null : values.instructor;
    const startDate = values.startDate
      ? format(values.startDate, "yyyy-MM-dd")
      : null;
    const endDate = values.endDate
      ? format(values.endDate, "yyyy-MM-dd")
      : null;

    const result = await api.getReportByInstructor(
      module,
      instructor,
      quizType,
      startDate,
      endDate
    );

    const modules = result.data || [];

    if (!modules.length) {
      toast.error(t("General.no_data"));
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Cover worksheet
    const coverSheet = XLSX.utils.json_to_sheet([]);
    setCoverData(coverSheet);
    XLSX.utils.book_append_sheet(workbook, coverSheet, "Cover");

    // Data worksheet

    modules.forEach((module) => {
      // create a new worksheet for each module
      const worksheet = XLSX.utils.json_to_sheet([]);

      const columns = [
        { label: t("Reports.fields.instructor_name"), key: "instructor_name" },
        { label: t("Reports.fields.instructor_id"), key: "instructor_id" },
        { label: t("Reports.fields.period"), key: "period" },
        { label: t("Reports.fields.module"), key: "module" },
        { label: t("Reports.fields.total_score"), key: "avg_score" },
      ];

      const topics = [];
      // get all topics from all instructors
      module.instructors.forEach((instructor) => {
        instructor.topics.forEach((topic) => {
          if (!topics.find((t) => t.topicId === topic.topicId)) {
            topics.push(topic);
          }
        });
      });

      // add topics to the columns in the same order
      topics.forEach((topic, index) => {
        columns.push({ label: topic.topicName, key: topic.topicId });
      });

      const excelData = [];
      // add data for each instructor
      module.instructors.forEach((instructor) => {
        const period =
          instructor.startDate && instructor.endDate
            ? `${moment(instructor.startDate).format("DD/MM/YYYY")} - ${moment(
                instructor.endDate
              ).format("DD/MM/YYYY")}`
            : "";

        const row = [
          `${instructor.lastName} ${instructor.firstName}`,
          instructor.employee_id,
          period || "",
          module.moduleName,
          Math.round((instructor.total_score || 0) * 100) / 100 + "%",
        ];

        topics.forEach((topic) => {
          const topicScore = instructor.topics.find(
            (t) => t.topicId === topic.topicId
          );
          row.push(
            topicScore ? Math.round(topicScore.score * 100) / 100 + "%" : ""
          );
        });

        excelData.push(row);
      });

      XLSX.utils.sheet_add_aoa(worksheet, [
        columns.map((column) => column.label),
      ]);
      XLSX.utils.sheet_add_json(worksheet, excelData, {
        origin: "A2",
        skipHeader: true,
      });

      if (module.moduleName.length > 31) {
        module.moduleName = module.moduleName.substring(0, 28) + "...";
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, module.moduleName);
    });

    // Export Excel file
    const fileName = t("Reports.per_instructor_title");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);

    // const fileType =
    //   "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=UTF-8";
    // const fileExtension = ".xlsx";
    // const fileName = t("Reports.per_instructor_title");

    // // const ws = XLSX.utils.json_to_sheet(excelData);
    // const ws2 = XLSX.utils.json_to_sheet(coverData);
    // const wb = {
    //   Sheets: { cover: ws2 },
    //   SheetNames: ["cover", "data"],
    // };
    // const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    // const data = new Blob([excelBuffer], { type: fileType });
    // FileSaver.saveAs(data, fileName + fileExtension);
  };

  return (
    <>
      <H4 pb={2}>{t("Reports.per_instructor_title")}</H4>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1} alignContent="center" alignItems={"center"}>
          <Grid item>
            <AppTextField
              select
              name="module"
              onChange={(e) => setFieldValue("module", e.target.value)}
              disabled={false}
              sx={{ width: 220 }}
              label={t("Reports.fields.module")}
              SelectProps={{
                IconComponent: KeyboardArrowDown,
                value: values.module,
              }}
              error={Boolean(touched.module && errors.module)}
              helperText={touched.module && errors.module}
            >
              <div></div>
              {modules?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </AppTextField>
          </Grid>
          <Grid item sx={{ mr: 1 }}>
            <AppTextField
              select
              name="instructor"
              onChange={(e) => setFieldValue("instructor", e.target.value)}
              disabled={false}
              sx={{ width: 220 }}
              label={t("Reports.fields.instructor")}
              SelectProps={{
                IconComponent: KeyboardArrowDown,
                value: values.instructor,
              }}
              error={Boolean(touched.instructor && errors.instructor)}
              helperText={touched.instructor && errors.instructor}
            >
              <div></div>
              {instructors?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </AppTextField>
          </Grid>
          <Grid item>
            {/* Date range picker */}
            <CustomDateRangePicker
              open={open}
              setOpen={setOpen}
              start={values.startDate}
              end={values.endDate}
              setStart={(value) => setFieldValue(`startDate`, value)}
              setEnd={(value) => setFieldValue(`endDate`, value)}
              minDate={false}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
            >
              {t("Reports.export")}
            </Button>

            {/* <ExportExcel
                excelData={data}
                coverData={coverData}
                fileName={t("Reports.per_group_title")}
                disabled={values.group ? false : true}
              /> */}
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default ReportPerInstructor;
